import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Button, ProgressIndicator, ErrorBanner } from "rhombus-react";

import { fetchAssetDetail } from "reducers/Assets";
import { closeModal } from "reducers/Modal";

import { API_URL, ASSET_TYPES } from "utils/constants";

import "./AssetPreviewBlade.css";

export default function AssetPreviewBlade() {
	const dispatch = useDispatch();
	const { asset, libraryId } = useSelector(state => ({
		asset: state.modal.modalOptions.asset,
		libraryId: state.libraryProperties.id,
	}));
	const { assetType } = asset;
	const [isFullScreen, setIsFullScreen] = useState(false);
	const [assetDetail, setAssetDetail] = useState({});
	const [isLoading, setIsLoading] = useState(assetType === ASSET_TYPES.IMAGE);
	const [error, setError] = useState("");

	const hasError = error.length > 0;

	useEffect(() => {
		const shouldLoadDetails = assetType === ASSET_TYPES.VIDEO && asset.encoded; // We need to fetch the url for encoded videos

		if (shouldLoadDetails) {
			setIsLoading(shouldLoadDetails);
			dispatch(fetchAssetDetail(libraryId, asset.assetType, asset.id))
				.then((resp) => {
					setIsLoading(false);
					if (resp.success && resp.payload?.data) {
						setAssetDetail(resp.payload.data);
					} else {
						let message = resp.payload?.errorObj?.description || "Error loading asset";
						setError(message);
					}
				});
		}
	}, []);

	function onToggleFullScreen() {
		setIsFullScreen(!isFullScreen);
	}

	function onClose() {
		dispatch(closeModal());
	}

	function onError() {
		setIsLoading(false);
		setError("Asset failed to load");
	}

	function renderContent() {
		let content;
		let errorDisplay;

		if (hasError) {
			errorDisplay = (
				<ErrorBanner title={error} />
			);
		}

		const base = `${API_URL}/crm/v1/`;
		let url = '';

		/**
		 * Since encoded video assets require asset details, we wait to insert the element until
		 * we are done fetching.
		 *
		 * We cannot bail early, since images use their onLoad event to set state.
		 */

		if (assetType === ASSET_TYPES.IMAGE) {
			url = `${base}creative-libraries/${libraryId}/images/show/${asset.name}`;
			content = (
				<img
					src={url}
					alt={`Preview of ${asset.name}`}
					onLoad={() => setIsLoading(false)}
					onError={onError}
				/>
			);
		} else if (assetType === ASSET_TYPES.VIDEO && !isLoading) {
			if (asset.encoded) {
				url = `https://${assetDetail.url}`;
			} else {
				url = `${base}asset-manager/${libraryId}/videos/${asset.name}`;
			}

			content = (
				<video src={url} onError={onError} controls />
			);
		} else if (assetType === ASSET_TYPES.RICHLOAD) {
			url = `${base}asset-manager/${libraryId}/richloads/${asset.name}?download=false`;
			content = <iframe src={url} height="100%" onError={onError} />;
		} else if (assetType === ASSET_TYPES.AUDIO) {
			url = `${base}ui/asset-manager/${libraryId}/audios/${asset.name}`;
			content = <audio src={url} onError={onError} controls />;
		} else if(assetType === ASSET_TYPES.AFTEREFFECTS){
			content = <video src={`https://${asset.url}`} onError={onError} controls />;
		}

		return (
			<div className="asset-preview-container">
				{errorDisplay}
				<ProgressIndicator isLoading={isLoading} />
				<div className="asset-preview-content">{content}</div>
			</div>
		);
	}
	function renderFooter() {
		return (
			<Button theme="default" onClick={onClose} className="close-btn">
				Close
			</Button>
		);
	}

	const modalProps = {
		title: asset.name,
		type: "blade",
		className: "creative-preview-blade",
		setAppTitle: false,

		xClose: true,
		allowFullScreen: true,
		isFullScreen,
		onToggleFullScreen,

		renderFooter,
		renderContent,

		onClose,
	};
	return <Modal {...modalProps} />;
}
